import React from 'react';
import * as fabric from 'fabric';
import { SHAPE_VARIANT_NAMES } from '../../../utils/toothShapes';
import { SHADE_PRESETS, DESIGN_PRESETS } from '../../../utils/smileTemplates';
import { ungroupOnCanvas } from '../SmileDesignerCanvas';

const ToothDesignPanel = ({ designParams, onDesignParamsChange, onApplyPreset, onRegenerateTeeth, fabricCanvas, onSaveState }) => {
  const updateParam = (key, value) => {
    onDesignParamsChange(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 mb-3">Tooth Design</h3>

      {/* Show/Hide Teeth Toggle */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium text-gray-700">Show Teeth Overlay</span>
        <button
          onClick={() => updateParam('showTeeth', !designParams.showTeeth)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            designParams.showTeeth ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
            designParams.showTeeth ? 'translate-x-4' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      {/* Design Presets */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-700 mb-1.5 block">Design Preset</label>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.values(DESIGN_PRESETS).map(preset => (
            <button
              key={preset.id}
              onClick={() => onApplyPreset(preset)}
              className={`px-2 py-1.5 text-[11px] font-medium rounded-md border transition ${
                designParams.preset === preset.id
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tooth Shape Variant */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-700 mb-1.5 block">Tooth Shape</label>
        <div className="flex gap-1.5">
          {Object.entries(SHAPE_VARIANT_NAMES).map(([key, name]) => (
            <button
              key={key}
              onClick={() => updateParam('toothVariant', key)}
              className={`flex-1 px-2 py-1.5 text-[11px] font-medium rounded-md border transition ${
                (designParams.toothVariant || 'natural') === key
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Shade Presets */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-700 mb-1.5 block">Tooth Shade</label>
        <div className="flex gap-1.5">
          {SHADE_PRESETS.map(shade => (
            <button
              key={shade.id}
              onClick={() => updateParam('toothColor', shade.color)}
              className={`flex-1 flex flex-col items-center gap-1 p-1.5 rounded-md border transition ${
                designParams.toothColor === shade.color
                  ? 'border-blue-400 ring-1 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              title={shade.description}
            >
              <div
                className="w-5 h-5 rounded-full border border-gray-300"
                style={{ backgroundColor: shade.color }}
              />
              <span className="text-[9px] text-gray-500">{shade.name}</span>
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <label className="text-[10px] text-gray-500">Custom:</label>
          <input
            type="color"
            value={designParams.toothColor}
            onChange={(e) => updateParam('toothColor', e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border-0"
          />
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-3">
        <SliderControl
          label="Central Width"
          value={designParams.centralWidth}
          min={0.08}
          max={0.25}
          step={0.005}
          onChange={(v) => updateParam('centralWidth', v)}
          displayValue={`${Math.round(designParams.centralWidth * 100)}%`}
        />
        <SliderControl
          label="Lateral Ratio"
          value={designParams.lateralRatio}
          min={0.4}
          max={0.85}
          step={0.01}
          onChange={(v) => updateParam('lateralRatio', v)}
          displayValue={designParams.lateralRatio.toFixed(2)}
        />
        <SliderControl
          label="Canine Ratio"
          value={designParams.canineRatio}
          min={0.25}
          max={0.65}
          step={0.01}
          onChange={(v) => updateParam('canineRatio', v)}
          displayValue={designParams.canineRatio.toFixed(2)}
        />
        <SliderControl
          label="Smile Arc"
          value={designParams.smileArcCurvature}
          min={0}
          max={0.08}
          step={0.005}
          onChange={(v) => updateParam('smileArcCurvature', v)}
          displayValue={designParams.smileArcCurvature.toFixed(3)}
        />
        <SliderControl
          label="Opacity"
          value={designParams.toothOpacity}
          min={0.1}
          max={1.0}
          step={0.05}
          onChange={(v) => updateParam('toothOpacity', v)}
          displayValue={`${Math.round(designParams.toothOpacity * 100)}%`}
        />
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-medium text-gray-600">Teeth Per Side</label>
            <span className="text-[11px] text-gray-400">{designParams.teethPerSide}</span>
          </div>
          <input
            type="range"
            min={3}
            max={5}
            step={1}
            value={designParams.teethPerSide}
            onChange={(e) => updateParam('teethPerSide', parseInt(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>
      </div>

      {/* Regenerate button */}
      <button
        onClick={onRegenerateTeeth}
        className="mt-4 w-full px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition"
      >
        Regenerate Teeth
      </button>

      {/* Interaction hint */}
      <p className="mt-3 text-[10px] text-gray-400 text-center leading-tight">
        Teeth are grouped — drag to reposition all. Double-click to ungroup for individual editing.
      </p>

      {/* Re-group / Ungroup controls */}
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => {
            if (!fabricCanvas) return;
            const group = fabricCanvas.getObjects().find(o => o.customType === 'toothGroup');
            if (group) {
              ungroupOnCanvas(fabricCanvas, group);
              if (onSaveState) onSaveState();
            }
          }}
          className="flex-1 px-2 py-1.5 text-[11px] font-medium text-gray-600 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition"
          title="Ungroup to edit individual teeth"
        >
          Ungroup
        </button>
        <button
          onClick={() => {
            if (!fabricCanvas) return;
            const teeth = fabricCanvas.getObjects().filter(o => o.customType === 'tooth');
            if (teeth.length === 0) return;
            fabricCanvas.discardActiveObject();
            const group = new fabric.Group(teeth);
            teeth.forEach(t => fabricCanvas.remove(t));
            group.customType = 'toothGroup';
            fabricCanvas.add(group);
            fabricCanvas.setActiveObject(group);
            fabricCanvas.requestRenderAll();
            if (onSaveState) onSaveState();
          }}
          className="flex-1 px-2 py-1.5 text-[11px] font-medium text-gray-600 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition"
          title="Re-group teeth to move together"
        >
          Re-group
        </button>
      </div>
    </div>
  );
};

const SliderControl = ({ label, value, min, max, step, onChange, displayValue }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-[11px] font-medium text-gray-600">{label}</label>
      <span className="text-[11px] text-gray-400">{displayValue}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
    />
  </div>
);

export default ToothDesignPanel;
